import { Col, DatePicker, Divider, Form, Input, InputNumber, Row, Select, Typography } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { CountryCodes } from "enums/countryCodes";
import { Genders } from "enums/genders";
import { PersonStatuses } from "enums/personStatuses";
import { useArrayDropdown } from "hooks/useArrayDropdown";
import { useEnumDropdown } from "hooks/useEnumDropdown";
import { useCreatePerson } from "hooks/usePersonsApi";
import { cloneDeep } from "lodash-es";
import { useIntl } from "react-intl";
import { DATE_FORMAT } from "~app/constants";

type Props = {
  entityId: string;
  onCreated: (person: any) => void;
};

export const CreatePersonForm = ({ entityId, onCreated }: Props) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();

  const dropDownNationalities = useEnumDropdown(CountryCodes, "countryCode", true);
  const dropDownGenders = useArrayDropdown([Genders.Male, Genders.Female, Genders.Unknown], "gender", false);

  const { mutateAsync: createPerson, isPending: isPersonCreating } = useCreatePerson();

  const onCreatePersonHandler = async () => {
    const formData = await form.validateFields();
    const data = cloneDeep(formData);

    data.dcEntityId = entityId;
    data.force = false;

    const response = await createPerson({ data });

    onCreated?.(response?.data?.[0]);
  };

  return (
    <Row className="mt-25">
      <Col span={24}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            gender: Genders.Male,
            status: PersonStatuses.Active,
          }}
        >
          <Row>
            <Col span={19}>
              <Typography.Text type="secondary" strong>
                {formatMessage({
                  id: "createPersonModal.personDetails.section.title",
                })}
              </Typography.Text>
              <Divider className="mt-5" />
              <Row gutter={40}>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.nameFullLocal",
                    })}
                    name="nameFullLocal"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.nameFullLatin",
                    })}
                    name="nameFullLatin"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.abbreviatedName",
                    })}
                    name="abbreviatedName"
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.dateOfBirth",
                    })}
                    name="dateOfBirth"
                  >
                    <DatePicker format={DATE_FORMAT} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.nationality",
                    })}
                    name="nationality"
                  >
                    <Select showSearch options={dropDownNationalities} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    rules={[{ required: true }]}
                    label={formatMessage({
                      id: "createPersonModal.fields.gender",
                    })}
                    name="gender"
                  >
                    <Select showSearch options={dropDownGenders} />
                  </Form.Item>
                </Col>
              </Row>

              <Typography.Text type="secondary" strong>
                {formatMessage({
                  id: "createPersonModal.additionalDetails.section.title",
                })}
              </Typography.Text>
              <Divider className="mt-5" />
              <Row gutter={40}>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.height",
                    })}
                    name="height"
                  >
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={formatMessage({
                      id: "createPersonModal.fields.weight",
                    })}
                    name="weight"
                  >
                    <InputNumber style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row justify="end">
            <DefaultButton loading={isPersonCreating} onClick={onCreatePersonHandler}>
              {formatMessage({ id: "button.createPerson" })}
            </DefaultButton>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};
